import { useSession } from '@faststore/sdk'
import { BK_ID_STORAGE_KEY } from 'src/sdk/session/validate'

import { useSyncExternalStore } from 'react'

let overrideToken: string | undefined // impostato dal callback di Algolia
let algoliaSubscribed = false
let consentSubscribed = false
const listeners = new Set<() => void>()

export const notifyTokenStore = () => listeners.forEach((l) => l())

/* ---------------- CONSENT ---------------- */

const readConsentCookie = (): boolean | undefined => {
  if (typeof document === 'undefined') return undefined

  const entry = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith('CookieConsent='))

  if (!entry) return undefined

  const value = decodeURIComponent(entry.slice('CookieConsent='.length))
  const match = value.match(/marketing:(true|false)/)

  return match ? match[1] === 'true' : undefined
}

export const getHasMarketingConsent = (): boolean => {
  if (typeof window === 'undefined') return false

  if (window.Cookiebot?.hasResponse) {
    return window.Cookiebot.consent?.marketing === true
  }

  return readConsentCookie() ?? false
}

function ensureConsentSubscribed() {
  if (consentSubscribed || typeof window === 'undefined') return
  consentSubscribed = true

  window.addEventListener('CookiebotOnConsentReady', notifyTokenStore)
  window.addEventListener('CookiebotOnAccept', notifyTokenStore)
  window.addEventListener('CookiebotOnDecline', notifyTokenStore)

  // modifiche a bk_id fatte da un'altra tab
  window.addEventListener('storage', (e) => {
    if (e.key === BK_ID_STORAGE_KEY || e.key === null) notifyTokenStore()
  })
}

/* ---------------- ALGOLIA ---------------- */

export function ensureAlgoliaSubscribed() {
  if (
    algoliaSubscribed ||
    typeof window === 'undefined' ||
    typeof window.aa !== 'function'
  )
    return
  algoliaSubscribed = true

  window.aa?.(
    'onUserTokenChange',
    (token?: string) => {
      overrideToken = token
      notifyTokenStore()
    },
    { immediate: true }
  )

  window.aa?.('getUserToken', null, (_err: unknown, token?: string) => {
    if (token && token !== overrideToken) {
      overrideToken = token
      notifyTokenStore()
    }
  })
}

export const resetOverrideToken = () => {
  overrideToken = undefined
  notifyTokenStore()
}

const readHasConsent = (): boolean =>
  process.env.NODE_ENV === 'development' || getHasMarketingConsent()

export const useHasMarketingConsent = () =>
  useSyncExternalStore(subscribe, readHasConsent, () => false)

/* ---------------- STORE ---------------- */

// identità stabile: va bene per useSyncExternalStore
const subscribe = (onChange: () => void) => {
  ensureConsentSubscribed()
  ensureAlgoliaSubscribed()
  listeners.add(onChange)

  return () => {
    listeners.delete(onChange)
  }
}

/* ---------------- USER TOKEN ---------------- */

const readCookieToken = (): string | undefined => {
  if (typeof document === 'undefined') return undefined

  const entry = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith('_ALGOLIA='))

  return entry
    ? decodeURIComponent(entry.slice(entry.indexOf('=') + 1))
    : undefined
}

// snapshot PURO: nessun side effect
const readUserToken = (): string | undefined => {
  if (process.env.NODE_ENV !== 'development' && !getHasMarketingConsent())
    return undefined

  return overrideToken ?? readCookieToken()
}

// Utilizzabile ovunque (fuori da React, prima di una chiamata GA4, ecc.)
export const getUserToken = (): string | undefined => {
  ensureConsentSubscribed()
  ensureAlgoliaSubscribed()

  return readUserToken()
}

export const useUserToken = () =>
  useSyncExternalStore(subscribe, readUserToken, () => undefined)

/* ---------------- AUTHENTICATED USER TOKEN ---------------- */

const readBkId = (): string | undefined => {
  if (typeof window === 'undefined') return undefined

  try {
    return (
      JSON.parse(localStorage.getItem(BK_ID_STORAGE_KEY) || '{}').bk_id ||
      undefined
    )
  } catch {
    return undefined
  }
}

// snapshot puro: bk_id + consenso
const readAuthSnapshot = (): string | undefined =>
  getHasMarketingConsent() ? readBkId() : undefined

// Fuori da React: `isLoggedIn` va passato da chi ha accesso alla session
export const getAuthenticatedUserToken = (
  isLoggedIn: boolean
): string | undefined => {
  ensureConsentSubscribed()

  return isLoggedIn ? readAuthSnapshot() : undefined
}

export const useAuthenticatedUserToken = () => {
  const { person } = useSession()
  const bkId = useSyncExternalStore(
    subscribe,
    readAuthSnapshot,
    () => undefined
  )

  return person ? bkId : undefined
}
